import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import axios from "axios";
import moment from "moment";
import { API_BASE_URL } from "../../config";
import * as enquiryService from "../services/manageEnquiryService";
import { canViewAllLeads, currentUserId } from "../utils/role";
import { authHeaders,getSessionToken } from "../utils/session";

const token = () => getSessionToken();
const headers = () => ({
  ...authHeaders(),
  "Content-Type": "application/json",
});

/**
 * Lead `status` holds an `option_value` from the `lead_follow_up_status` master
 * (GET /lead-follow-up-options), NOT an ad-hoc code:
 *
 *   0 Follow Up · 1 Hot Lead · 2 No Response · 3 Interested · 4 Mark for Future
 *   5 Not Interested · 6 Meeting Scheduled · 7 Converted · 10 Negative
 *
 * 8 (Not Responding) and 9 (Closed) were retired from the master — nothing may
 * hardcode them again. Legacy rows can still carry them, so a missing label must
 * never render blank: the screens fall back to the lead's stage.
 *
 * Note 2 is "No Response", not Negative — Negative is 10, which is what the
 * Negative list itself filters on (negativeenquiry.jsx: status === "10").
 */
export const LEAD_STATUS = {
  FUTURE: "4",    // Mark for Future
  CONVERTED: "7", // Done
  NEGATIVE: "10",
};

/**
 * Pull the API's own error message out of an axios failure.
 *
 * The backend returns a real reason in the response body even on non-2xx
 * (e.g. 404 + { status: false, message: "This user does not have a lead
 * assignment configuration." }). Axios only puts "Request failed with status
 * code 404" on err.message, so read the body first and fall back afterwards.
 */
export const apiErrorMessage = (err, fallback = "Something went wrong") => {
  const data = err?.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (data.message) return data.message;
    if (data.error) return data.error;
    // Laravel-style { errors: { field: ["msg"] } }
    const firstFieldError = Object.values(data.errors || {})[0];
    if (Array.isArray(firstFieldError) && firstFieldError[0]) return firstFieldError[0];
  }
  return err?.message || fallback;
};

// ==================== QUERY HOOKS ====================

/**
 * Hook to fetch paginated enquiry list with filters
 */
export const useEnquiryList = (page, filterData) => {
  return useQuery({
    queryKey: ["enquiryList", page, filterData],
    queryFn: () => enquiryService.fetchEnquiryList(page, filterData),
    staleTime: 0,
    keepPreviousData: true,
    retry: 2,
    refetchOnMount: "always",
  });
};

/**
 * Hook to fetch single enquiry details
 */
export const useEnquiryDetails = (id) => {
  return useQuery({
    queryKey: ["enquiryDetails", id],
    queryFn: () => enquiryService.fetchEnquiryDetails(id),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
};

/**
 Fetch internal user list
 */
export const useInternalUserList = () =>
  useQuery({
    queryKey: ["internalUserList", "all"],
    // Every consumer (team tree, transfer target picker, assignee filter) wants
    // the complete list — never a page of it.
    queryFn: () => enquiryService.fetchInternalUserList(1, 100, { all: true }),
    select: (res) => res.data, // unwrap → plain array
    staleTime: 5 * 60 * 1000, // cache 5 min (list rarely changes)
  });

/**
 * Alias kept so existing code that still imports useAdminList keeps working.
 * Both hooks hit the same /get-internal-user-list endpoint.
 */
export const useAdminList = useInternalUserList;

/**
 * The internal users whose leads the logged-in user is entitled to see.
 *
 * A manager owns their own queue *and* their reportees' — so the Inbox is
 * scoped to the whole reporting subtree below the logged-in user, not just to
 * them. Super-admin / admin oversee everyone, so they get the full list.
 *
 * There is no "my team" endpoint; /get-internal-user-list already carries
 * `reporting_to_user_id`, so the tree is built from that.
 */
export const useMyTeam = () => {
  const query = useInternalUserList();
  const users = query.data || [];
  const meId = currentUserId();

  const members = useMemo(() => {
    if (!users.length) return [];
    if (canViewAllLeads()) return users;

    const childrenOf = new Map();
    users.forEach((u) => {
      const parent = String(u.reporting_to_user_id ?? "");
      if (!childrenOf.has(parent)) childrenOf.set(parent, []);
      childrenOf.get(parent).push(u);
    });

    const out = [];
    const seen = new Set();
    const me = users.find((u) => String(u.id) === String(meId));
    if (me) { seen.add(String(me.id)); out.push(me); }

    // Walk down every level — a manager's reportees may have reportees too.
    // `seen` also guards against a cyclic reporting chain in the data.
    const walk = (id) => {
      (childrenOf.get(String(id)) || []).forEach((u) => {
        if (seen.has(String(u.id))) return;
        seen.add(String(u.id));
        out.push(u);
        walk(u.id);
      });
    };
    walk(meId);
    return out;
  }, [users, meId]);

  return { ...query, data: members };
};

// /**
//  * Hook to allocate selected leads to an internal user.
//  * Endpoint : POST /manual-asign-lead-to-user
//  * Payload  : { internal_user_id: number, lead_ids: number[] }
//  */
// export const useAllocateLeads = () => {
//   const queryClient = useQueryClient();

//   return useMutation({
//     mutationFn: ({ adminId, leadIds }) =>
//       enquiryService.allocateLeads({ internal_user_id: adminId, lead_ids: leadIds }),
//     onSuccess: () => {
//       // Refresh the lead list so "Assigned To" column updates immediately
//       queryClient.invalidateQueries({ queryKey: ["enquiryList"] });
//       // Also bust the internal-user cache so lead counts re-fetch on next open
//       queryClient.invalidateQueries({ queryKey: ["internalUserList"] });
//     },
//     onError: (error) => {
//       toast.error(error?.response?.data?.message || error.message || "Failed to allocate leads");
//     },
//   });
// };

/**
 * Hook to fetch lead follow-up status options from API.
 * Endpoint: GET /lead-follow-up-options
 * Returns array of { id, category, label, option_value, is_active, ... }
 */
export const useFollowUpOptions = () => {
  return useQuery({
    queryKey: ["followUpOptions"],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/lead-follow-up-options`, {
        headers: headers(),
      });
      return res.data?.data || [];
    },
    staleTime: 10 * 60 * 1000,
    retry: 2,
  });
};

/**
 * Unread inbound-mail counts per lead, keyed by lead id.
 *
 * The lead list endpoint knows nothing about mail, so the unread badge has to
 * come from the inbox endpoint and be overlaid on top of the rows — the same
 * overlay the Lead Inbox screen does.
 *
 * `scopeUserId`: pass the logged-in user's id to scope the request to them.
 * Pass "" (team / super-admin view) to ask for everything the token can see.
 */
export const useLeadUnreadCounts = (scopeUserId = "") => {
  return useQuery({
    queryKey: ["lead-unread", String(scopeUserId || "all")],
    queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/lead-inbox/list`, {
        params: {
          page: 1,
          limit: 200,
          ...(scopeUserId ? { internal_user_id: Number(scopeUserId) } : {}),
        },
        headers: headers(),
      });
      const raw = res.data?.data ?? res.data;
      const list = Array.isArray(raw) ? raw : [];
      const map = {};
      list.forEach((item) => {
        const id = item.lead_id || item.id;
        if (!id) return;
        map[String(id)] = {
          unread: Number(item.unread_count ?? item.unread_email_count ?? 0),
          last_email_at: item.last_email_at || item.last_reply_at || null,
        };
      });
      return map;
    },
    staleTime: 30 * 1000,
    retry: 1,
  });
};

/**
 * Mark a lead's inbound mail as read.
 *
 * Opening the lead IS reading it, so the badge has to clear from wherever the
 * lead was opened — not only from the Email Communication screen.
 */
export const useMarkLeadRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (leadId) => {
      const res = await axios.patch(
        `${API_BASE_URL}/lead-inbox/mark-read/${leadId}`,
        { internal_user_id: Number(localStorage.getItem("trade_user_id")) || null },
        { headers: headers() },
      );
      return res.data;
    },
    // Clear the badge straight away; the server call only confirms it.
    onMutate: (leadId) => {
      queryClient.setQueriesData({ queryKey: ["lead-unread"] }, (old) =>
        old && old[String(leadId)]
          ? { ...old, [String(leadId)]: { ...old[String(leadId)], unread: 0 } }
          : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["lead-unread"] });
    },
    // Silent: failing to clear a badge is not worth a toast in the user's face.
    onError: () => {},
  });
};

/**
 * The options a user may PICK.
 *
 * "Converted" is no longer chosen by hand — a lead lands in Done only after a
 * subscription is created for it. It is filtered out here rather than in every
 * dropdown, while useFollowUpOptions keeps returning the full master so leads
 * that already carry status 7 still render the right label.
 */
export const useSelectableFollowUpOptions = () => {
  const query = useFollowUpOptions();
  const options = query.data;
  const data = useMemo(
    () => (options || []).filter((o) => String(o.option_value) !== LEAD_STATUS.CONVERTED),
    [options],
  );
  return { ...query, data };
};

export const useAllocateLeads = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ adminId, leadIds }) => {
      let res;
      try {
        res = await axios.post(
          `${API_BASE_URL}/manual-asign-lead-to-user`,
          { internal_user_id: adminId, lead_ids: leadIds },
          { headers: headers() },
        );
      } catch (err) {
        // Non-2xx (404/422/500…): surface the API's own message, not axios's
        // generic "Request failed with status code NNN".
        throw new Error(apiErrorMessage(err, "Failed to allocate leads"));
      }

      if (
        res.data &&
        (res.data.status === false ||
          res.data.status === 0 ||
          res.data.status === 400 ||
          res.data.status === 500)
      ) {
        throw new Error(res.data.message || "Allocation failed");
      }

      return res.data;
    },

    // No toast here on purpose — every caller either shows its own Swal alert
    // (list pages) or its own toast (EnquiryFollowUp auto-assign). Toasting from
    // the hook too would double up on the Swal screens.
    onSuccess: () => {
      /* refetchType: "all" is the important bit — by default React Query only
         refetches ACTIVE queries and just marks the rest stale. The list and the
         Kanban board use different query keys (the board asks for bigger pages),
         so whichever one isn't on screen kept serving its cached rows and the
         new assignee only appeared after a hard reload. */
      qc.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
      qc.invalidateQueries({ queryKey: ["internalUserList"], refetchType: "all" });
      qc.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });
      // Allocated leads leave the Unassigned list
      qc.invalidateQueries({ queryKey: ["unassigned-leads"], refetchType: "all" });
      /* The follow-up panel reads the lead from ["lead-details", id]. Without
         this, a lead taken over from the list still opened with the OLD owner and
         kept showing the take-over gate until a hard reload. */
      qc.invalidateQueries({ queryKey: ["lead-details"], refetchType: "all" });
    },
  });
};

// Fetch lead with follow-ups
export const useLeadDetailsFollowUp = (id) => {
  return useQuery({
    queryKey: ["lead-details", id],
    queryFn: () => enquiryService.fetchLeadDetails(id),
    enabled: !!id,
    select: (res) => res.data,
  });
};

// ── NEW: Inbox leads (assigned to current admin) ─────────────────────────────
/** One page of the Inbox list — shared by the paged and the infinite hook. */
const fetchInboxPage = async (page, filters = {}) => {
  const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
    headers: headers(),
    params: {
      page,
      limit: filters.limit || 10,
      is_assigned: 1,
      /* The API scopes by token: a rep gets their own leads, a manager their
         team's, a super-admin everything. So no owner is forced here — assign_to
         only travels when the user picks one in the filter, to narrow the view. */
      ...(filters.assign_to ? { assign_to: filters.assign_to } : {}),
      // The API owns kanban_column, so it filters on it too.
      ...(filters.kanban_column ? { kanban_column: filters.kanban_column } : {}),
      // Due & Overdue reads next_followup_date, which only the API can evaluate
      // across every page.
      ...(filters.due_filter ? { due_filter: filters.due_filter } : {}),
      search: filters.search || "",
      start_date: filters.start_date || "",
      end_date: filters.end_date || "",
      is_positive: filters.is_positive || "",
    },
  });

  const rows = res.data?.data || [];
  return {
    data: rows,
    // Column headers: the set currently on screen, minus the column filter.
    counts: res.data?.counts || null,
    /* Chip totals live on the BASE set (every filter except the chips' own), so
       All / Due & Overdue / Untouched keep their numbers while switching between
       them. `due_counts` is the older shape, kept as a fallback. */
    chipCounts: res.data?.chip_counts || null,
    dueCounts: res.data?.due_counts || null,
    total:
      res.data?.total_records ??
      res.data?.pagination?.total_records ??
      rows.length,
    totalPages:
      res.data?.pagination?.total_pages ??
      (Math.ceil(rows.length / (filters.limit || 10)) || 1),
  };
};

/**
 * Inbox list with infinite scroll.
 *
 * useInfiniteQuery (not a manual page counter + accumulator) because it refetches
 * EVERY loaded page on invalidation. With the hand-rolled version only the page
 * the user was on refreshed, so a lead edited from the panel kept its old stage /
 * status on any earlier page until a hard reload.
 */
export const useInboxLeadsInfinite = (filters = {}) => {
  return useInfiniteQuery({
    queryKey: ["inbox-leads", "infinite", filters],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => fetchInboxPage(pageParam, filters),
    getNextPageParam: (lastPage, pages) =>
      pages.length < (lastPage?.totalPages || 1) ? pages.length + 1 : undefined,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

export const useInboxLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["inbox-leads", page, filters],
   queryFn: async () => {
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: headers(),
        params: {
          page,
          // The Kanban board classifies every lead into a column, so a 10-row
          // page would show wrong counts there — callers ask for a bigger page.
          limit: filters.limit || 10,
          is_assigned: 1,
          /* The API scopes by token: a rep gets their own leads, a manager their
             team's, a super-admin everything. So no owner is forced here —
             assign_to only travels when the user picks one in the filter, to
             narrow the view. */
          ...(filters.assign_to ? { assign_to: filters.assign_to } : {}),
          /* Stage filtering server-side: the API owns kanban_column, so it can
             also filter on it. The client-side filter stays as a safety net for
             the case where the param is ignored. */
          ...(filters.kanban_column ? { kanban_column: filters.kanban_column } : {}),
          // "Due & Overdue" is a server filter too — it reads next_followup_date,
          // which only the API can evaluate across every page.
          ...(filters.due_filter ? { due_filter: filters.due_filter } : {}),
          search: filters.search || "",
          start_date: filters.start_date || "",
          end_date: filters.end_date || "",
          is_positive: filters.is_positive || "",
        },
      });

     const allData = res.data?.data || [];

      /* The Inbox is this user's complete assigned pipeline — nothing is filtered
         out. The Kanban board needs every stage present (New / Contacted / Future
         / Done / Negative) so the user can see what they converted, what went
         negative and who they have already contacted. Scoping is done purely by
         assign_to in the request params above. */
      const openLeads = allData;

      /* Order comes from the API (newest first, and it owns the due filter now).
         The old client-side due-rank sort only reordered the current page, so the
         list disagreed with both the response and the board. */
      return {
        data: openLeads,
        // Per-column totals when the API sends them — the board headers then
        // count the whole set instead of the rows that happen to be loaded.
        counts: res.data?.counts || null,
        /* Chip totals live on the BASE set (every filter except the chips' own),
           so All / Due & Overdue / Untouched keep their numbers while switching
           between them. `due_counts` is the older shape, kept as a fallback. */
        chipCounts: res.data?.chip_counts || null,
        dueCounts: res.data?.due_counts || null,
        total:
          res.data?.total_records ??
          res.data?.pagination?.total_records ??
          openLeads.length,
        totalPages:
          res.data?.pagination?.total_pages ??
          (Math.ceil(openLeads.length / (filters.limit || 10)) || 1),
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// Fetch future leads
export const useFutureLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["future-leads", page, filters],
    queryFn: async () => {
      // Scoping comes from the token now — no owner is forced from the client.
      // assign_to travels only when the user narrows the list to one person.
      const res = await axios.get(`${API_BASE_URL}/get-trademark-enquiries`, {
        headers: headers(),
        params: {
          page,
          limit: 10,
          status: 4,
          ...(filters.assign_to ? { assign_to: filters.assign_to } : {}),
          search: filters.search || "",
          start_date: filters.start_date || "",
          end_date: filters.end_date || "",
        },
      });
       const allData = res.data?.data || [];
      const today = moment().format("YYYY-MM-DD");

      const futureData = allData.filter((item) => {
        const nfd = item.next_followup_date;
        const isMarkedFuture = String(item.status) === "4";

        if (!nfd) return isMarkedFuture;

        // Parse DD-MM-YYYY to YYYY-MM-DD for reliable comparison
        const parsed = moment(nfd, "DD-MM-YYYY");
        if (!parsed.isValid()) return isMarkedFuture;

        const nfdFormatted = parsed.format("YYYY-MM-DD");

        // Show if: date is in the future OR status is "4"
        return nfdFormatted > today || isMarkedFuture;
      });

      /* Server totals, not page totals: the screen scrolls through pages now, so
         it needs to know whether another page exists. `data` stays client-filtered
         (the API has no "future" filter yet), so a page can legitimately return
         fewer rows than it fetched. */
      const payload = res.data || {};
      return {
        data: futureData,
        total: Number(payload.total_records ?? futureData.length),
        totalPages: payload.pagination?.total_pages ?? 1,
        fetchedCount: allData.length,
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};


// ── NEW: Done / Converted leads ──────────────────────────────────────────────
export const useDoneLeads = (page, filters = {}) => {
  return useQuery({
    queryKey: ["done-leads", page, filters],
    queryFn: async () => {
      /* Dedicated Done endpoint: a lead is here only because a subscription
         exists for it, so the filters are subscription filters — the general
         lead list can't express those. */
      const res = await axios.get(`${API_BASE_URL}/converted-leads`, {
        headers: headers(),
        params: {
          page,
          // The board classifies every converted lead into a bucket, so a
          // 10-row page would show wrong column counts.
          limit: filters.limit || 10,
          search: filters.search || "",
          // The two pickers read as "subscription starts on/after X" and
          // "subscription ends on/before Y".
          subscription_start_from: filters.start_date || "",
          subscription_end_to: filters.end_date || "",
          ...(filters.bucket ? { bucket: filters.bucket } : {}),
          ...(filters.assign_to ? { assign_to: filters.assign_to } : {}),
        },
      });

      const payload = res.data || {};
      const rows = Array.isArray(payload.data) ? payload.data : [];
      const total = Number(payload.total_records ?? rows.length);

      return {
        data: rows,
        total,
        // Per-bucket totals for the board headers. Absent → the board counts
        // the rows it has.
        counts: payload.counts || null,
        totalPages:
          payload.pagination?.total_pages ??
          (Math.ceil(total / (filters.limit || 10)) || 1),
      };
    },
    keepPreviousData: true,
    staleTime: 0,
    refetchOnMount: "always",
  });
};

// ── NEW: Mark lead as "future" ───────────────────────────────────────────────
export const useMarkFuture = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }) => {
      const res = await axios.post(
        `${API_BASE_URL}/update-lead-stage`,
        { id, stage: "future", remarks: remarks || "" },
        { headers: headers() },
      );
      if (res.data && res.data.status === false)
        throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead marked for future follow-up");
      qc.invalidateQueries({ queryKey: ["enquiry-list"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
      qc.invalidateQueries({ queryKey: ["future-leads"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to mark as future"),
  });
};

// ── NEW: Mark lead as "converted" / done ─────────────────────────────────────
export const useMarkDone = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, remarks }) => {
      const res = await axios.post(
        `${API_BASE_URL}/update-lead-stage`,
        { id, stage: "converted", remarks: remarks || "" },
        { headers: headers() },
      );
      if (res.data && res.data.status === false)
        throw new Error(res.data.message);
      return res.data;
    },
    onSuccess: () => {
      toast.success("Lead marked as converted!");
      qc.invalidateQueries({ queryKey: ["enquiry-list"] });
      qc.invalidateQueries({ queryKey: ["inbox-leads"] });
      qc.invalidateQueries({ queryKey: ["future-leads"] });
      qc.invalidateQueries({ queryKey: ["done-leads"] });
    },
    onError: (err) => toast.error(err?.message || "Failed to mark as done"),
  });
};

// ==================== MUTATION HOOKS ====================

/**
 * Hook to delete one or multiple enquiries
 */
export const useDeleteEnquiry = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enquiryService.deleteEnquiry,
    onSuccess: () => {
      // refetchType: "all" — the list the user is not currently looking at is
      // inactive, and inactive queries are only marked stale, never refetched.
      queryClient.invalidateQueries({
        queryKey: ["enquiryList"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["inbox-leads"],
        refetchType: "all",
      });
    },
    onError: (error) => {
      toast.error(error.message || "Failed to delete enquiry");
    },
  });
};

/**
 * Hook to update enquiry status
 */
export const useUpdateEnquiryStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: enquiryService.updateEnquiryStatus,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["enquiryList"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["inbox-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["future-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["done-leads"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({
        queryKey: ["negative-leads"],
        refetchType: "all",
      });
      /* The follow-up panel reads its timeline and its "Scheduled Follow-Ups"
         card from these two. The backend stamps the new status onto the lead's
         latest follow-up, so without busting them the sidebar kept showing the
         previous status until a reload. */
      queryClient.invalidateQueries({ queryKey: ["lead-followups"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["lead-details"], refetchType: "all" });
      toast.success("Lead status updated successfully!");
    },
    onError: (error) => {
      return
      // toast.error(error.message || "Failed to update enquiry status");
    },
  });
};

/**
 * Hook to import enquiries from Excel file
 */
export const useImportEnquiries = () => {
  const queryClient = useQueryClient();

  /** Bullet list of row reasons, capped so a 500-row file cannot fill the screen. */
  const asList = (rows, max = 25) => {
    const shown = rows.slice(0, max);
    const rest = rows.length - shown.length;
    return (
      `<ul style="text-align:left;margin:0;padding-left:18px;max-height:260px;overflow:auto">` +
      shown.map((r) => `<li style="margin-bottom:4px">${r}</li>`).join("") +
      (rest > 0 ? `<li style="list-style:none;color:#6b7280">…and ${rest} more</li>` : "") +
      `</ul>`
    );
  };

  return useMutation({
    mutationFn: enquiryService.importEnquiries,
    onSuccess: (result) => {
      /* refetchType: "all" — the List and the Kanban board use different query
         keys, so the one that isn't on screen would keep its cached rows and the
         imported leads only appeared after a reload. Imported leads arrive
         unassigned, so the Unassigned list has to be busted too. */
      queryClient.invalidateQueries({ queryKey: ["enquiryList"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["unassigned-leads"], refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: ["inbox-leads"], refetchType: "all" });

      const { imported, skippedCount, skipped, message } = result || {};

      /* Skipped rows are duplicates, not errors — the file was fine, those leads
         simply already exist. They get one dialog (never a dialog AND a toast). */
      if (skippedCount > 0) {
        Swal.fire({
          icon: imported > 0 ? "success" : "info",
          title:
            imported > 0
              ? `${imported} lead${imported !== 1 ? "s" : ""} imported`
              : "No new leads",
          html:
            `<p style="margin:0 0 10px">${
              imported > 0
                ? `${skippedCount} row${skippedCount !== 1 ? "s" : ""} skipped because they already exist:`
                : message || `All ${skippedCount} rows already exist.`
            }</p>` + (skipped.length ? asList(skipped) : ""),
          confirmButtonColor: "#667eea",
          width: 620,
        });
        return;
      }

      if (imported > 0) {
        toast.success(
          `${imported} lead${imported !== 1 ? "s" : ""} imported — they are unassigned, find them in Unassigned Leads.`,
        );
      } else {
        // Nothing imported and nothing skipped: unusual, so just relay the API.
        toast.info(message || "Nothing was imported.");
      }
    },
    onError: (error) => {
      /* Row-wise validation: the whole file was rejected, so the user has to fix
         it and upload the SAME file again — no duplicate risk. A list needs a
         dialog, not a toast. */
      if (Array.isArray(error?.rows) && error.rows.length) {
        Swal.fire({
          icon: "error",
          title: "Import rejected",
          html:
            `<p style="margin:0 0 10px;text-align:left">${error.message}</p>` +
            asList(error.rows) +
            `<p style="margin:12px 0 0;text-align:left;color:#6b7280;font-size:13px">Nothing was imported — fix these rows and upload the same file again.</p>`,
          confirmButtonColor: "#dc2626",
          width: 680,
        });
        return;
      }
      // File missing / empty / server error — a single line is enough.
      toast.error(
        error?.isServerError
          ? "Import failed, please try again."
          : apiErrorMessage(error, "Failed to import enquiries"),
      );
    },
  });
};
